import type {
  StaffImportApprovalBlockerCode,
  StaffImportDecisionKind,
  StaffImportProcessingState,
  StaffImportReviewRefusalCode,
  staffImportIssueCodes,
  staffImportMatchBases,
  staffImportProcessingErrorCodes,
  staffImportRowOutcomes,
} from "@lafayette/schemas";

// Every sentence of the review and processing screens comes from a CODE in this file. Nothing here (and
// nothing built from it) copies a cell, a file name or a URL, so no message can leak source text into a
// toast, a log or an error. A `Record` over the contract's code tuple makes the compiler fail when the
// contract gains a code that has no sentence.

type Tuple<T extends readonly string[]> = T[number];

export type RowOutcome = Tuple<typeof staffImportRowOutcomes>;

export type OutcomeView = Readonly<{
  label: string;
  glyph: string;
  tone: "ok" | "warning" | "danger" | "neutral" | "info";
}>;

/** A row outcome as a glyph and a word: it never depends on colour alone. `null` is a row not validated yet. */
export const outcomeViews: Readonly<Record<RowOutcome, OutcomeView>> = {
  READY: { label: "Ready", glyph: "✓", tone: "ok" },
  WARNING: { label: "Warning", glyph: "⚠", tone: "warning" },
  INVALID: { label: "Invalid", glyph: "✕", tone: "danger" },
  DUPLICATE_CANDIDATE: { label: "Possible duplicate", glyph: "⧉", tone: "warning" },
  APPROVED: { label: "Approved", glyph: "●", tone: "info" },
  IMPORTED: { label: "Imported", glyph: "✓", tone: "ok" },
  SKIPPED: { label: "Skipped", glyph: "–", tone: "neutral" },
};

export const pendingOutcomeView: OutcomeView = {
  label: "Not checked yet",
  glyph: "…",
  tone: "neutral",
};

export function outcomeView(outcome: RowOutcome | null): OutcomeView {
  return outcome === null ? pendingOutcomeView : outcomeViews[outcome];
}

export const outcomeFilterOptions: readonly Readonly<{ value: RowOutcome; label: string }>[] = [
  { value: "READY", label: "Ready" },
  { value: "WARNING", label: "Warning" },
  { value: "INVALID", label: "Invalid" },
  { value: "DUPLICATE_CANDIDATE", label: "Possible duplicate" },
  { value: "APPROVED", label: "Approved" },
  { value: "IMPORTED", label: "Imported" },
  { value: "SKIPPED", label: "Skipped" },
];

export const issueMessages: Readonly<Record<Tuple<typeof staffImportIssueCodes>, string>> = {
  REQUIRED_FIELD_MISSING: "A required value is missing.",
  INVALID_FORMAT: "A value has a format the importer cannot read.",
  UNKNOWN_CODE: "A value is not a known code.",
  OUT_OF_RANGE: "A value is outside the allowed range.",
  INVALID_TIME_ZONE: "The time zone is missing or not valid.",
  INVALID_INTERVAL: "The start and end times do not make a valid interval.",
  PRICING_INCONSISTENT: "The price values do not agree with each other.",
  DUPLICATE_IN_FILE: "Another row in this file holds the same item.",
  POSSIBLE_DUPLICATE: "An item that already exists may be the same one.",
  UNSUPPORTED_VALUE: "A value is not supported.",
  AMBIGUOUS_CURRENCY: "The currency is not clear. Choose it.",
  AMBIGUOUS_VALUE: "A value can be read in more than one way.",
  TIME_ZONE_INFERRED: "The time zone was guessed from the city. Confirm it.",
  MODEL_ASSISTED: "A model suggested a value.",
  DESTRUCTIVE_UPDATE: "The update replaces values that the item already has.",
  PLACE_ID_INVALID: "The Google Place ID has a format the importer cannot read. It was ignored.",
  MATCH_SIGNALS_DISAGREE:
    "The Google Place ID and the source key point at different items. Check which item is right.",
  PLACE_ID_HELD_BY_OTHER_ITEM:
    "Another item already holds this Google Place ID. The new item was created without it.",
  PLACE_ID_CONFLICT:
    "The item already has a different Google Place ID. The import kept the old one.",
};

export const matchBasisLabels: Readonly<Record<Tuple<typeof staffImportMatchBases>, string>> = {
  EXTERNAL_PROVIDER_ID: "Google Place ID",
  SOURCE_ID: "Same source key",
  NAME_LOCATION: "Same name and place",
  FUZZY_CANDIDATE: "Similar name",
};

export const matchStrengthLabels = {
  STRONG: "Strong match (an identifier is equal)",
  WEAK: "Weak match (needs your decision)",
} as const;

export const decisionKindLabels: Readonly<Record<StaffImportDecisionKind, string>> = {
  CURRENCY: "Currency",
  TIME_ZONE: "Time zone",
  DATE_ORDER: "Date order",
  TAXONOMY: "Category or tag",
  DUPLICATE_ACTION: "Duplicate",
  DESTRUCTIVE_UPDATE: "Replace existing values",
};

export type ProcessingView = Readonly<{
  label: string;
  glyph: string;
  tone: OutcomeView["tone"];
}>;

export const processingViews: Readonly<Record<StaffImportProcessingState, ProcessingView>> = {
  WAITING: { label: "Waiting", glyph: "…", tone: "neutral" },
  RETRYING: { label: "Retrying", glyph: "↻", tone: "warning" },
  FAILED: { label: "Failed", glyph: "✕", tone: "danger" },
  CREATED: { label: "Created", glyph: "✓", tone: "ok" },
  UPDATED: { label: "Updated", glyph: "✓", tone: "ok" },
  SKIPPED: { label: "Skipped", glyph: "–", tone: "neutral" },
};

export const processingFilterOptions: readonly Readonly<{
  value: StaffImportProcessingState;
  label: string;
}>[] = [
  { value: "CREATED", label: "Created" },
  { value: "UPDATED", label: "Updated" },
  { value: "SKIPPED", label: "Skipped" },
  { value: "FAILED", label: "Failed" },
  { value: "RETRYING", label: "Retrying" },
  { value: "WAITING", label: "Waiting" },
];

/** What went wrong with a row, and what staff can do. A code, never text from the file. */
export const processingErrorMessages: Readonly<
  Record<Tuple<typeof staffImportProcessingErrorCodes>, string>
> = {
  ITEM_CHANGED_SINCE_REVIEW:
    "The existing item was changed after you reviewed this row. Retry the import and review the row again.",
  DECISION_MISSING: "A decision this row needs is missing. Retry the import and decide again.",
  DESTRUCTIVE_NOT_CONFIRMED:
    "The values this row replaces changed after you confirmed them. Retry the import and confirm again.",
  SOURCE_TAKEN: "Another item already uses the source key of this row.",
  TAXONOMY_MISSING: "A category or tag this row uses no longer exists.",
  ITEM_NOT_EDITABLE: "The existing item cannot be changed by an import. Edit it in the catalog.",
  CONSTRAINT: "The catalog refused a value of this row.",
  LOCK_TIMEOUT: "The catalog was busy. The row will be tried again.",
  INTERNAL:
    "An internal error stopped this row. Retry the import. If it fails again, tell support.",
};

export const refusalMessages: Readonly<Record<StaffImportReviewRefusalCode, string>> = {
  ROW_NOT_FOUND: "a row was not found",
  ROW_STATE: "a row cannot change in its current state",
  DECISION_MISSING: "a row still needs a decision",
  INVALID_ROW: "an invalid row cannot be approved",
  CANDIDATE_UNKNOWN: "the chosen existing item is not a candidate of the row",
};

export const blockerMessages: Readonly<
  Record<StaffImportApprovalBlockerCode, (count: number) => string>
> = {
  ROWS_UNDECIDED: (count) =>
    `${count} ${count === 1 ? "row is" : "rows are"} not approved or skipped yet.`,
  NO_APPROVED_ROWS: () => "No row is approved. Approve at least one row.",
  DECISIONS_OPEN: (count) =>
    `${count} ${count === 1 ? "row still needs" : "rows still need"} a decision.`,
};

export const jobFailureMessages = {
  FILE_UNREADABLE: "The stored file could not be read.",
  LIMIT_EXCEEDED: "The file is larger than the limits allow.",
  VALIDATION_ABORTED: "The validation stopped before it finished.",
  PROCESSING_ERROR: "The import stopped because some rows could not be processed.",
  REJECTED_BY_STAFF: "This import was closed by staff. Nothing reached the catalog.",
} as const;

export const intentStateLabels = {
  PENDING: "Queued",
  CLAIMED: "Running",
  SUCCEEDED: "Finished",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
} as const;

const number = new Intl.NumberFormat("en-US");
const formatCount = (value: number): string => number.format(value);

export const plural = (count: number, one: string, many: string): string =>
  `${formatCount(count)} ${count === 1 ? one : many}`;
